import React from "react";
import {
  Box,
  Stepper,
  Step,
  StepButton,
  StepConnector,
  Typography,
  stepConnectorClasses,
  stepLabelClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";

export interface StepItem {
  label: string;
  icon: React.ReactNode;
  /** Renders the step muted and non-interactive, and labels it "Skipped". */
  skipped?: boolean;
}

export interface StyledStepperProps {
  steps: StepItem[];
  activeStep: number;
  onStepChange: (step: number) => void;
}

const VerticalConnector = styled(StepConnector)(() => ({
  [`& .${stepConnectorClasses.line}`]: {
    borderTop: "none",
    borderLeft: "1.5px dashed #E2E8F0",
    height: "24px",
    marginLeft: "calc(50% - 1px)",
  },
}));

const StepCircle = styled("div")<{
  ownerState: { active: boolean; completed: boolean; skipped: boolean };
}>(({ ownerState }) => {
  const isActive = ownerState.active;
  const isCompleted = ownerState.completed;

  let bgColor = "#F8FAFC";
  let borderColor = "#E2E8F0";
  let textColor = "#94A3B8";
  let shadow = "none";

  if (ownerState.skipped) {
    textColor = "#CBD5E1";
  } else if (isCompleted) {
    bgColor = "#0078D4";
    borderColor = "#0078D4";
    textColor = "#fff";
    shadow = "0 2px 4px rgba(0, 120, 212, 0.2)";
  } else if (isActive) {
    bgColor = "#0078D4";
    borderColor = "#0078D4";
    textColor = "#fff";
    shadow = "0 0 0 3px rgba(0, 120, 212, 0.12), 0 2px 8px rgba(0, 120, 212, 0.25)";
  }

  return {
    width: 40,
    height: 40,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: bgColor,
    border: `1.5px solid ${borderColor}`,
    color: textColor,
    position: "relative",
    zIndex: 2,
    transition: "all 0.25s ease",
    boxShadow: shadow,
    flexShrink: 0,
  };
});

const StyledStepper: React.FC<StyledStepperProps> = ({
  steps,
  activeStep,
  onStepChange,
}) => {
  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: "999px",
        padding: "8px 16px",
        mx: "auto",
        display: "inline-flex",
        width: "fit-content !important",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04), 0 4px 12px rgba(0, 0, 0, 0.06)",
        border: "1px solid #F1F5F9",
      }}
    >
      <Stepper connector={<VerticalConnector />} activeStep={activeStep}>
        {steps.map((step, index) => {
          const skipped = !!step.skipped;
          const completed = index < activeStep && !skipped;
          const active = index === activeStep && !skipped;
          return (
            <Step
              key={step.label}
              sx={{
                flex: "0 1 auto",
                width: "fit-content",
                overflow: "visible",
                margin: "4px 2px",
                [`& .${stepLabelClasses.iconContainer}`]: {
                  display: "none",
                },
              }}
            >
              <StepButton
                onClick={() => onStepChange(index)}
                icon={null}
                disabled={skipped}
                sx={{
                  textAlign: "left",
                  pl: 0,
                  borderRadius: "16px",
                  overflow: "hidden",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: active ? "transparent" : "rgba(0, 120, 212, 0.04)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    pl: 1.5,
                    pr: 2,
                    py: 0.5,
                  }}
                >
                  <StepCircle ownerState={{ active, completed, skipped }}>
                    {completed ? <CheckIcon sx={{ fontSize: 18 }} /> : step.icon}
                  </StepCircle>

                  <Box sx={{ ml: 1.5, textAlign: "left" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: skipped ? "#CBD5E1" : active ? "#0078D4" : "#94A3B8",
                        fontWeight: 500,
                        fontSize: "11px",
                        letterSpacing: "0.4px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        lineHeight: 1,
                      }}
                      className={s.stepIndicator}
                    >
                      {skipped ? "Skipped" : `Step ${index + 1}`}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "nowrap",
                        fontWeight: active ? 600 : 500,
                        color: skipped
                          ? "#CBD5E1"
                          : active
                          ? "#0F172A"
                          : completed
                          ? "#475569"
                          : "#64748B",
                        textDecoration: skipped ? "line-through" : "none",
                        lineHeight: 1.2,
                        mt: 0.5,
                        fontSize: "14px",
                      }}
                      className={s.stepLabel}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                </Box>
              </StepButton>
            </Step>
          );
        })}
      </Stepper>
    </Box>
  );
};

export default withStyles(s)(StyledStepper);
